import {FirebaseApp, getApps, initializeApp} from 'firebase/app';
import {
  Auth,
  User,
  browserLocalPersistence,
  getAuth,
  onAuthStateChanged,
  signOut,
  setPersistence,
} from 'firebase/auth';
import {
  ActionItem,
  ActionReviewDecision,
  ApplicationAnswer,
  ApprovedBullet,
  BootstrapPayload,
  CareerEvidence,
  CareerFact,
  Contact,
  CoverLetterGenerationResult,
  DiscoveryQuestionAnswer,
  DiscoveryResult,
  DocumentArtifact,
  ElicitationQuestion,
  ExtensionGrantResponse,
  ManualRecoveryResponse,
  OnboardingAnswerDraft,
  Opportunity,
  OpportunityStage,
  PageResult,
  Persona,
  PersonaQuerySuggestions,
  Relationship,
  RelationshipStage,
  WorkOperation,
} from './types';
import {
  firebaseOptionsFromRuntimeConfig,
  parseRuntimeConfig,
  type PursuitDeckRuntimeConfig,
} from './runtimeConfig';

const API_PREFIX = '/api';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let authPromise: Promise<Auth> | null = null;

export const loadRuntimeConfig = async (): Promise<PursuitDeckRuntimeConfig> => {
  const response = await fetch('/firebase-config.json', {
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new ApiError(response.status, 'firebase_config_unavailable', 'Sign-in configuration is unavailable.');
  }
  try {
    return parseRuntimeConfig(await response.json());
  } catch {
    throw new ApiError(503, 'firebase_config_invalid', 'Sign-in configuration is invalid.');
  }
};

export const resolveFirebaseAppName = ({appName, projectId}: Pick<PursuitDeckRuntimeConfig, 'appName' | 'projectId'>): string => {
  const configuredName = appName?.trim();
  if (configuredName) return configuredName;
  return `${projectId}-${window.location.hostname}`;
};

const currentOrNewApp = (config: PursuitDeckRuntimeConfig): FirebaseApp => {
  const resolvedName = resolveFirebaseAppName(config);
  const existingApp = getApps().find((app) => app.name === resolvedName);
  return existingApp ?? initializeApp(firebaseOptionsFromRuntimeConfig(config), resolvedName);
};

export const getPursuitDeckAuth = (): Promise<Auth> => {
  if (!authPromise) {
    authPromise = loadRuntimeConfig().then(async (config) => {
      const auth = getAuth(currentOrNewApp(config));
      await setPersistence(auth, browserLocalPersistence);
      return auth;
    });
  }
  return authPromise;
};

export const waitForAuthUser = async (): Promise<User | null> => {
  const auth = await getPursuitDeckAuth();
  if (auth.currentUser) return auth.currentUser;
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

export const signOutPursuitDeck = async (): Promise<void> => {
  await signOut(await getPursuitDeckAuth());
};

const readApiError = async (response: Response): Promise<ApiError> => {
  const fallback = response.status === 403 ? 'You do not have access to PursuitDeck.' : 'The request could not be completed.';
  try {
    const body = (await response.json()) as {
      error?: {code?: string; message?: string; details?: unknown};
      code?: string;
      message?: string;
    };
    return new ApiError(
      response.status,
      body.error?.code ?? body.code ?? 'request_failed',
      body.error?.message ?? body.message ?? fallback,
      body.error?.details,
    );
  } catch {
    return new ApiError(response.status, 'request_failed', fallback);
  }
};

export const fetchAuthenticatedBlob = async (
  path: string,
  options: {
    signal?: AbortSignal;
    fetchImpl?: typeof fetch;
    user?: Pick<User, 'getIdToken'> | null;
  } = {},
): Promise<Blob> => {
  const target = new URL(path, window.location.origin);
  if (
    target.origin !== window.location.origin ||
    !target.pathname.startsWith('/api/documents/')
  ) {
    throw new ApiError(400, 'document_url_invalid', 'The document URL is not a PursuitDeck API URL.');
  }
  const user = options.user === undefined ? await waitForAuthUser() : options.user;
  if (!user) {
    throw new ApiError(401, 'authentication_required', 'Sign in to open this document.');
  }
  const headers = new Headers({Accept: 'application/pdf'});
  headers.set('Authorization', 'Bearer ' + (await user.getIdToken()));
  const response = await (options.fetchImpl ?? fetch)(target.pathname + target.search, {
    method: 'GET',
    headers,
    credentials: 'same-origin',
    cache: 'no-store',
    redirect: 'error',
    ...(options.signal ? {signal: options.signal} : {}),
  });
  if (!response.ok) throw await readApiError(response);
  if ((response.headers.get('Content-Type') ?? '').split(';', 1)[0]!.trim().toLowerCase() !== 'application/pdf') {
    throw new ApiError(502, 'document_type_invalid', 'The protected document returned an unexpected file type.');
  }
  return response.blob();
};

export const fetchAuthenticatedExtensionPackage = async (
  options: {
    signal?: AbortSignal;
    fetchImpl?: typeof fetch;
    user?: Pick<User, 'getIdToken'> | null;
  } = {},
): Promise<Blob> => {
  const target = new URL('/api/extension/package', window.location.origin);
  if (
    target.origin !== window.location.origin ||
    target.pathname !== '/api/extension/package' ||
    target.search ||
    target.hash
  ) {
    throw new ApiError(400, 'extension_package_url_invalid', 'The extension package URL is invalid.');
  }
  const user = options.user === undefined ? await waitForAuthUser() : options.user;
  if (!user) {
    throw new ApiError(401, 'authentication_required', 'Sign in to download the extension package.');
  }
  const headers = new Headers({Accept: 'application/zip'});
  headers.set('Authorization', 'Bearer ' + (await user.getIdToken()));
  const response = await (options.fetchImpl ?? fetch)(target.pathname, {
    method: 'GET',
    headers,
    credentials: 'same-origin',
    cache: 'no-store',
    redirect: 'error',
    ...(options.signal ? {signal: options.signal} : {}),
  });
  if (!response.ok) throw await readApiError(response);
  if ((response.headers.get('Content-Type') ?? '').split(';', 1)[0]!.trim().toLowerCase() !== 'application/zip') {
    throw new ApiError(502, 'extension_package_type_invalid', 'The extension package returned an unexpected file type.');
  }
  return response.blob();
};

export const apiRequest = async <T>(
  path: string,
  init: RequestInit = {},
  options: {allowAnonymous?: boolean} = {},
): Promise<T> => {
  const user = await waitForAuthUser();
  if (!user && !options.allowAnonymous) {
    throw new ApiError(401, 'authentication_required', 'Sign in to continue.');
  }

  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (user) headers.set('Authorization', 'Bearer ' + (await user.getIdToken()));

  const response = await fetch(API_PREFIX + path, {
    ...init,
    headers,
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) throw await readApiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
};

export const collectCursorPages = async <T>(
  fetchPage: (cursor?: string) => Promise<PageResult<T>>,
): Promise<PageResult<T>> => {
  const items: T[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  for (let pageNumber = 0; pageNumber < 1_000; pageNumber += 1) {
    const result = await fetchPage(cursor);
    items.push(...result.items);
    if (!result.nextCursor) return {items};
    if (seenCursors.has(result.nextCursor)) {
      throw new ApiError(502, 'pagination_cursor_repeated', 'The server returned a repeated list cursor.');
    }
    seenCursors.add(result.nextCursor);
    cursor = result.nextCursor;
  }
  throw new ApiError(502, 'pagination_limit_exceeded', 'The list exceeded the safe pagination limit.');
};

const getPage = <T>(path: string): Promise<PageResult<T>> =>
  collectCursorPages((cursor) => {
    const separator = path.includes('?') ? '&' : '?';
    const cursorQuery = cursor ? '&cursor=' + encodeURIComponent(cursor) : '';
    return apiRequest<PageResult<T>>(`${path}${separator}limit=200${cursorQuery}`);
  });

export const api = {
  session: () => apiRequest<BootstrapPayload['user']>('/session'),
  bootstrap: () => apiRequest<BootstrapPayload>('/bootstrap'),
  updateNotificationConsent: (state: BootstrapPayload['notifications']['state']) =>
    apiRequest<BootstrapPayload['notifications']>('/notifications/consent', {
      method: 'PUT',
      body: JSON.stringify({state}),
    }),
  acceptBetaNotice: () =>
    apiRequest<BootstrapPayload>('/onboarding/accept', {method: 'POST', body: JSON.stringify({accepted: true})}),
  uploadResume: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    form.append('kind', 'source_resume');
    return apiRequest<DocumentArtifact>('/onboarding/upload', {method: 'POST', body: form});
  },
  getQuestions: () =>
    apiRequest<{round: 1 | 2; questions: ElicitationQuestion[]}>('/onboarding/questions', {method: 'POST'}),
  reopenOnboarding: () =>
    apiRequest<BootstrapPayload['onboarding']>('/onboarding/reopen', {
      method: 'POST', body: JSON.stringify({}),
    }),
  setReducedQualityMode: (enabled: boolean) =>
    apiRequest<BootstrapPayload['onboarding']>('/onboarding/reduced-quality', {
      method: 'POST', body: JSON.stringify({enabled}),
    }),
  getOnboardingAnswerDraft: (round: 1 | 2) =>
    apiRequest<OnboardingAnswerDraft>(`/onboarding/answer-draft?round=${round}`),
  saveOnboardingAnswerDraft: (round: 1 | 2, answers: Record<string, string>) =>
    apiRequest<OnboardingAnswerDraft>('/onboarding/answer-draft', {
      method: 'PUT',
      body: JSON.stringify({round, answers}),
    }),
  submitAnswers: (round: 1 | 2, answers: Record<string, string>) =>
    apiRequest<{round: number; questions?: ElicitationQuestion[]; profileReady: boolean}>('/onboarding/answers', {
      method: 'POST',
      body: JSON.stringify({round, answers}),
    }),
  issueExtensionGrant: () => apiRequest<ExtensionGrantResponse>('/extension/grants', {
    method: 'POST',
    body: JSON.stringify({}),
  }),

  addTextEvidence: (input: {label: string; text: string}) =>
    apiRequest<CareerEvidence>('/career/evidence', {
      method: 'POST',
      body: JSON.stringify({kind: 'text', ...input}),
    }),
  addLinkEvidence: (input: {label: string; url: string}) =>
    apiRequest<CareerEvidence>('/career/evidence', {
      method: 'POST',
      body: JSON.stringify({kind: 'link', ...input}),
    }),
  uploadEvidence: (file: File, kind: 'document' | 'voice', label: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    form.append('label', label);
    return apiRequest<CareerEvidence>('/career/evidence/upload', {method: 'POST', body: form});
  },
  approveTranscript: (id: string, transcript: string) =>
    apiRequest<CareerEvidence>('/career/evidence/' + encodeURIComponent(id) + '/transcript', {
      method: 'POST',
      body: JSON.stringify({transcript, approved: true}),
    }),

  opportunities: () => getPage<Opportunity>('/opportunities'),
  createOpportunity: (input: Partial<Opportunity>) =>
    apiRequest<Opportunity>('/opportunities', {method: 'POST', body: JSON.stringify(input)}),
  moveOpportunity: (id: string, stage: OpportunityStage) =>
    apiRequest<Opportunity>('/opportunities/' + encodeURIComponent(id) + '/stage', {
      method: 'POST',
      body: JSON.stringify({stage}),
    }),
  requestCoverLetter: (id: string) =>
    apiRequest<CoverLetterGenerationResult>('/opportunities/' + encodeURIComponent(id) + '/cover-letter', {
      method: 'POST',
      body: JSON.stringify({trigger: 'user_requested'}),
    }),

  actionItems: () => getPage<ActionItem>('/action-items'),
  createActionItem: (input: Omit<ActionItem, 'id' | 'createdAt'>) =>
    apiRequest<ActionItem>('/action-items', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateActionItem: (id: string, patch: Pick<ActionItem, 'state'> & {snoozedUntil?: string}) =>
    apiRequest<ActionItem>('/action-items/' + encodeURIComponent(id), {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  decideActionItem: (id: string, decision: ActionReviewDecision) =>
    apiRequest<ActionItem>('/action-items/' + encodeURIComponent(id) + '/decision', {
      method: 'POST',
      body: JSON.stringify(decision),
    }),
  contacts: () => getPage<Contact>('/contacts'),

  discoveryResults: () => getPage<DiscoveryResult>('/discovery/results'),
  runDiscovery: () => apiRequest<{state: string; runId: string; operationId: string}>('/discovery/run', {method: 'POST'}),
  importManualPosting: (input: {
    canonicalUrl: string;
    companyName: string;
    title: string;
    description: string;
    location?: string;
    compensation?: string;
  }) => apiRequest<{state: 'created' | 'duplicate'; discoveryResultId: string}>('/discovery/manual', {
    method: 'POST',
    body: JSON.stringify(input),
  }),
  triageDiscovery: (id: string, state: DiscoveryResult['state']) =>
    apiRequest<DiscoveryResult>('/discovery/results/' + encodeURIComponent(id), {
      method: 'PATCH',
      body: JSON.stringify({state}),
    }),
  convertDiscoveryResult: (id: string) =>
    apiRequest<{opportunity: Opportunity; discoveryResult: DiscoveryResult}>(
      '/discovery/results/' + encodeURIComponent(id) + '/convert',
      {method: 'POST', body: JSON.stringify({})},
    ),
  askDiscoveryResult: (id: string, question: string) =>
    apiRequest<DiscoveryQuestionAnswer>('/discovery/results/' + encodeURIComponent(id) + '/questions', {
      method: 'POST',
      body: JSON.stringify({question}),
    }),


  relationships: () => getPage<Relationship>('/relationships'),
  createRelationship: (input: Partial<Relationship>) =>
    apiRequest<Relationship>('/relationships', {method: 'POST', body: JSON.stringify(input)}),
  updateRelationship: (id: string, input: Partial<Relationship>) =>
    apiRequest<Relationship>('/relationships/' + encodeURIComponent(id), {method: 'PATCH', body: JSON.stringify(input)}),
  moveRelationship: (id: string, stage: RelationshipStage) =>
    apiRequest<Relationship>('/relationships/' + encodeURIComponent(id) + '/stage', {
      method: 'POST',
      body: JSON.stringify({stage}),
    }),

  facts: () => getPage<CareerFact>('/career/facts'),
  createFact: (input: Omit<CareerFact, 'id' | 'updatedAt'>) =>
    apiRequest<CareerFact>('/career/facts', {method: 'POST', body: JSON.stringify(input)}),
  updateFact: (id: string, input: Partial<CareerFact>) =>
    apiRequest<CareerFact>('/career/facts/' + encodeURIComponent(id), {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  bullets: () => getPage<ApprovedBullet>('/career/bullets'),
  createBullet: (input: Omit<ApprovedBullet, 'id' | 'version' | 'updatedAt'>) =>
    apiRequest<ApprovedBullet>('/career/bullets', {method: 'POST', body: JSON.stringify(input)}),
  updateBullet: (id: string, input: Partial<ApprovedBullet>) =>
    apiRequest<ApprovedBullet>('/career/bullets/' + encodeURIComponent(id), {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  personas: () => getPage<Persona>('/personas'),
  createPersona: (input: Partial<Persona>) =>
    apiRequest<Persona>('/personas', {method: 'POST', body: JSON.stringify(input)}),
  updatePersona: (id: string, input: Partial<Persona>) =>
    apiRequest<Persona>('/personas/' + encodeURIComponent(id), {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  suggestPersonaQueries: (input: {
    roleFamily: string;
    seniority?: string;
    locationStrategy?: string;
    compensationMinimum?: number;
  }) => apiRequest<PersonaQuerySuggestions>('/personas/query-suggestions', {
    method: 'POST',
    body: JSON.stringify(input),
  }),
  applicationAnswers: () => getPage<ApplicationAnswer>('/application-answers'),
  createApplicationAnswer: (input: Omit<ApplicationAnswer, 'id' | 'updatedAt'>) =>
    apiRequest<ApplicationAnswer>('/application-answers', {method: 'POST', body: JSON.stringify(input)}),
  updateApplicationAnswer: (id: string, input: Partial<ApplicationAnswer>) =>
    apiRequest<ApplicationAnswer>('/application-answers/' + encodeURIComponent(id), {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  documents: () => getPage<DocumentArtifact>('/documents'),
  integrations: () => apiRequest<BootstrapPayload['integrations']>('/integrations'),
  operation: (id: string) => apiRequest<WorkOperation>('/operations/' + encodeURIComponent(id)),
  manualRecoveryPackage: (sessionId: string) =>
    apiRequest<ManualRecoveryResponse>('/extension/sessions/' + encodeURIComponent(sessionId) + '/manual-package'),
  startGoogleOAuth: (service: 'gmail' | 'calendar') =>
    apiRequest<{authorizationUrl: string}>('/oauth/google/start', {
      method: 'POST',
      body: JSON.stringify({service}),
    }),
  syncGmail: () => apiRequest<{state: WorkState; operationId: string}>('/integrations/gmail/sync', {method: 'POST'}),
  setGmailLabelRule: (approved: boolean) =>
    apiRequest<{approved: boolean; approvedAt?: string}>('/integrations/gmail/label-rule', {
      method: 'POST', body: JSON.stringify({approved}),
    }),
  disconnectIntegration: (provider: string) =>
    apiRequest<void>('/integrations/' + encodeURIComponent(provider), {method: 'DELETE'}),
};

type WorkState = import('./types').WorkState;
